import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, tokenStore } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  Trash2, Upload, FileText, Folder, FolderOpen, Search, Download,
  Edit, Save, X, FileImage, FileSpreadsheet, FileVideo, FileAudio, File as FileIcon, Archive
} from "lucide-react";

function fmtSize(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("ro-RO", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function docUrl(id, opts = {}) {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const token = tokenStore.get();
  const params = new URLSearchParams({ token: token || "" });
  if (opts.download) params.set("download", "1");
  return `${base}/api/documents/${id}?${params.toString()}`;
}

function FileTypeIcon({ contentType, filename, size = 18 }) {
  const ext = (filename || "").split(".").pop().toLowerCase();
  const ct = contentType || "";
  if (ct.startsWith("image/")) return <FileImage size={size} className="text-purple-600"/>;
  if (ct.startsWith("video/")) return <FileVideo size={size} className="text-pink-600"/>;
  if (ct.startsWith("audio/")) return <FileAudio size={size} className="text-orange-600"/>;
  if (ct.includes("pdf") || ext === "pdf") return <FileText size={size} className="text-red-600"/>;
  if (["xlsx", "xls", "csv"].includes(ext) || ct.includes("spreadsheet") || ct.includes("excel")) return <FileSpreadsheet size={size} className="text-emerald-600"/>;
  if (["docx", "doc"].includes(ext) || ct.includes("word")) return <FileText size={size} className="text-blue-600"/>;
  if (["zip", "rar", "7z"].includes(ext)) return <Archive size={size} className="text-amber-600"/>;
  return <FileIcon size={size} className="text-zinc-500"/>;
}

export default function Documente() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [folders, setFolders] = useState([]);
  const [items, setItems] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(""); // "" = toate
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);
  const [uploadFolder, setUploadFolder] = useState("");
  const [editFor, setEditFor] = useState(null); // doc object or null
  const inputRef = useRef(null);

  const loadFolders = async () => {
    try {
      const r = await api.get("/documents/folders");
      setFolders(r.data || []);
    } catch { setFolders([]); }
  };
  const loadItems = async () => {
    setLoading(true);
    try {
      const q = currentFolder ? `?folder=${encodeURIComponent(currentFolder)}` : "";
      const r = await api.get(`/documents${q}`);
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la încărcare");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (isAdmin) { loadFolders(); loadItems(); } /* eslint-disable-next-line */ }, [currentFolder, isAdmin]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => `${it.original_filename || ""} ${it.label || ""} ${it.note || ""} ${it.folder || ""}`.toLowerCase().includes(q));
  }, [items, search]);

  const totalSize = useMemo(() => items.reduce((s, it) => s + (Number(it.size) || 0), 0), [items]);

  if (!isAdmin) {
    return (
      <div className="card-flat p-8 text-center max-w-lg mx-auto mt-12" data-testid="documents-no-access">
        <FileText size={48} className="mx-auto text-zinc-300 mb-3"/>
        <div className="font-display text-xl font-bold mb-2">Acces restricționat</div>
        <div className="text-sm text-zinc-600">Doar administratorii pot accesa Documentele.</div>
      </div>
    );
  }

  const onPickFiles = () => inputRef.current?.click();

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    setUploadProgress(files.map((f) => ({ name: f.name, size: f.size, percent: 0, done: false, error: false })));
    let ok = 0, fail = 0;
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      try {
        const fd = new FormData();
        fd.append("file", f);
        if (uploadFolder.trim() || currentFolder) {
          fd.append("folder", uploadFolder.trim() || currentFolder);
        }
        await api.post("/documents/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            const total = evt.total || f.size;
            const percent = total ? Math.round((evt.loaded / total) * 100) : 0;
            setUploadProgress((prev) => prev.map((p, idx) => idx === i ? { ...p, percent } : p));
          },
        });
        setUploadProgress((prev) => prev.map((p, idx) => idx === i ? { ...p, percent: 100, done: true } : p));
        ok += 1;
      } catch (err) {
        const detail = err?.response?.data?.detail || err.message || "eroare";
        setUploadProgress((prev) => prev.map((p, idx) => idx === i ? { ...p, error: true, errorMsg: detail } : p));
        fail += 1;
      }
    }
    setUploading(false);
    if (ok) toast.success(`Încărcat: ${ok} document${ok === 1 ? "" : "e"}`);
    if (fail) toast.error(`Eșuat: ${fail} document${fail === 1 ? "" : "e"}`);
    e.target.value = "";
    await Promise.all([loadFolders(), loadItems()]);
    setTimeout(() => setUploadProgress([]), 2500);
  };

  const onDelete = async (it) => {
    if (!window.confirm(`Sigur ștergi „${it.original_filename}"?`)) return;
    try {
      await api.delete(`/documents/${it.id}`);
      toast.success("Document șters");
      loadFolders();
      loadItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la ștergere");
    }
  };

  const onSaveEdit = async (newData) => {
    try {
      await api.patch(`/documents/${editFor.id}`, newData);
      toast.success("Salvat");
      setEditFor(null);
      loadFolders();
      loadItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl" data-testid="documents-page">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <div>
          <h1 className="font-display text-3xl font-extrabold flex items-center gap-2">
            <FileText size={28} className="text-blue-600"/>
            Documente
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Arhivă privată admin · {items.length} document{items.length === 1 ? "" : "e"} · {fmtSize(totalSize)} total
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            value={uploadFolder}
            onChange={(e) => setUploadFolder(e.target.value)}
            placeholder="Folder (opțional)..."
            className="h-9 w-[180px] text-xs"
            list="docs-folders-list"
            data-testid="documents-upload-folder"
          />
          <datalist id="docs-folders-list">
            {folders.map((f) => <option key={f.key} value={f.key}/>)}
          </datalist>
          <Button
            onClick={onPickFiles}
            disabled={uploading}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="documents-upload-btn"
          >
            <Upload size={14} className="mr-1.5"/>
            {uploading ? "Se încarcă..." : "Încarcă documente"}
          </Button>
          <input
            ref={inputRef}
            type="file"
            multiple
            onChange={onUpload}
            className="hidden"
            data-testid="documents-file-input"
          />
        </div>
      </div>

      {/* Upload progress */}
      {uploadProgress.length > 0 && (
        <div className="card-flat p-3 mb-4 space-y-1.5" data-testid="documents-upload-progress">
          {uploadProgress.map((p, i) => (
            <div key={`${p.name}-${i}`} className="text-xs">
              <div className="flex items-center gap-2">
                <FileIcon size={12} className="text-zinc-400"/>
                <span className="flex-1 truncate font-mono">{p.name}</span>
                <span className="text-zinc-500 tabular-nums">{fmtSize(p.size)}</span>
                {p.error ? (
                  <span className="text-red-600 font-bold">✗ {p.errorMsg || "eșec"}</span>
                ) : p.done ? (
                  <span className="text-emerald-600 font-bold">✓ gata</span>
                ) : (
                  <span className="text-zinc-600 tabular-nums">{p.percent}%</span>
                )}
              </div>
              <div className="h-1 bg-zinc-100 rounded overflow-hidden">
                <div
                  className={`h-full transition-all ${p.error ? "bg-red-500" : p.done ? "bg-emerald-500" : "bg-blue-500"}`}
                  style={{ width: `${p.percent}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Folder tabs */}
      <div className="card-flat p-2 mb-4 flex items-center gap-1 flex-wrap" data-testid="documents-folder-tabs">
        <button
          type="button"
          onClick={() => setCurrentFolder("")}
          className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors flex items-center gap-1.5 ${currentFolder === "" ? "bg-blue-600 text-white" : "bg-white text-zinc-700 hover:bg-zinc-100 border border-zinc-200"}`}
          data-testid="documents-folder-all"
        >
          <FolderOpen size={12}/> Toate ({items.length})
        </button>
        {folders.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setCurrentFolder(f.key)}
            className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors flex items-center gap-1.5 ${currentFolder === f.key ? "bg-blue-600 text-white" : "bg-white text-zinc-700 hover:bg-zinc-100 border border-zinc-200"}`}
            data-testid={`documents-folder-${f.key || "none"}`}
          >
            <Folder size={12}/> {f.folder} ({f.count})
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-4 max-w-md">
        <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400"/>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Caută după nume, etichetă, notă..."
          className="pl-7 h-9"
          data-testid="documents-search"
        />
      </div>

      {/* Documents grid */}
      {loading ? (
        <div className="text-center py-12 text-zinc-500">Se încarcă...</div>
      ) : filtered.length === 0 ? (
        <div className="card-flat p-16 text-center" data-testid="documents-empty">
          <FileText size={48} className="mx-auto text-zinc-300 mb-3"/>
          <div className="font-display text-lg font-bold mb-1">
            {items.length === 0 ? "Niciun document încărcat" : "Nicio potrivire"}
          </div>
          <div className="text-sm text-zinc-500">
            {items.length === 0 ? `Apasă „Încarcă documente" pentru a începe.` : "Modifică filtrele sau termenul de căutare."}
          </div>
        </div>
      ) : (
        <div className="card-flat overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50">
                <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500">
                  <th className="px-3 py-2 w-8"></th>
                  <th className="px-3 py-2">Document</th>
                  <th className="px-3 py-2">Folder</th>
                  <th className="px-3 py-2 text-right">Mărime</th>
                  <th className="px-3 py-2">Încărcat de</th>
                  <th className="px-3 py-2">Data</th>
                  <th className="px-3 py-2 text-right">Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => (
                  <tr key={it.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`document-row-${it.id}`}>
                    <td className="px-3 py-2">
                      <FileTypeIcon contentType={it.content_type} filename={it.original_filename} size={20}/>
                    </td>
                    <td className="px-3 py-2">
                      <a
                        href={docUrl(it.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-blue-700 hover:underline"
                        data-testid={`document-view-${it.id}`}
                      >
                        {it.label || it.original_filename}
                      </a>
                      {it.label && (
                        <div className="text-[10px] text-zinc-500 font-mono truncate">{it.original_filename}</div>
                      )}
                      {it.note && (
                        <div className="text-[10px] text-zinc-600 italic mt-0.5">{it.note}</div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {it.folder ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-bold inline-flex items-center gap-1">
                          <Folder size={10}/> {it.folder}
                        </span>
                      ) : <span className="text-zinc-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right text-[11px] tabular-nums text-zinc-600">{fmtSize(it.size)}</td>
                    <td className="px-3 py-2 text-[11px] text-zinc-600">{it.uploaded_by_name}</td>
                    <td className="px-3 py-2 text-[11px] text-zinc-500 whitespace-nowrap">{fmtDate(it.created_at)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <a
                        href={docUrl(it.id, { download: true })}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] text-emerald-700 hover:bg-emerald-50"
                        title="Descarcă"
                        data-testid={`document-download-${it.id}`}
                      >
                        <Download size={11}/>
                      </a>
                      <button
                        type="button"
                        onClick={() => setEditFor(it)}
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] text-blue-700 hover:bg-blue-50"
                        title="Editează"
                        data-testid={`document-edit-${it.id}`}
                      >
                        <Edit size={11}/>
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(it)}
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] text-red-600 hover:bg-red-50"
                        title="Șterge"
                        data-testid={`document-delete-${it.id}`}
                      >
                        <Trash2 size={11}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editFor && (
        <EditDocDialog
          doc={editFor}
          folders={folders}
          onClose={() => setEditFor(null)}
          onSave={onSaveEdit}
        />
      )}
    </div>
  );
}

function EditDocDialog({ doc, folders, onClose, onSave }) {
  const [label, setLabel] = useState(doc.label || "");
  const [folder, setFolder] = useState(doc.folder || "");
  const [note, setNote] = useState(doc.note || "");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="document-edit-dialog">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-lg flex items-center gap-2">
            <Edit size={18}/> Editează document
          </h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700"><X size={18}/></button>
        </div>
        <div className="text-[10px] text-zinc-500 font-mono break-all mb-3">{doc.original_filename}</div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Etichetă (afișat în loc de numele fișierului)</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="ex: Contract Convic 2026" className="mt-1" data-testid="document-edit-label"/>
          </div>
          <div>
            <Label className="text-xs">Folder</Label>
            <Input value={folder} onChange={(e) => setFolder(e.target.value)} list="docs-edit-folders" placeholder="ex: contracte, facturi..." className="mt-1" data-testid="document-edit-folder"/>
            <datalist id="docs-edit-folders">
              {folders.map((f) => <option key={f.key} value={f.key}/>)}
            </datalist>
          </div>
          <div>
            <Label className="text-xs">Notă</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Observații..." className="mt-1 text-sm" data-testid="document-edit-note"/>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Anulează</Button>
          <Button
            onClick={() => onSave({ label, folder, note })}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="document-edit-save"
          >
            <Save size={14} className="mr-1.5"/> Salvează
          </Button>
        </div>
      </div>
    </div>
  );
}
